/**
 * B3 (E-1.B.1-03): Data Discovery step
 *
 * Fetch GHL entities, show counts, selectable checklist.
 * AI highlights problematic items.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import type { WizardState } from "../wizard-shell";

interface DiscoverStepProps {
  state: WizardState;
  goNext: () => void;
  updateState: (patch: Partial<WizardState>) => void;
}

const ENTITY_TYPES = [
  { key: "contacts", labelKey: "migration.discover.contacts", icon: "👥" },
  { key: "pipelines", labelKey: "migration.discover.pipelines", icon: "📊" },
  { key: "automations", labelKey: "migration.discover.automations", icon: "⚡" },
  { key: "tags", labelKey: "migration.discover.tags", icon: "🏷️" },
  { key: "customFields", labelKey: "migration.discover.customFields", icon: "📋" },
  { key: "campaigns", labelKey: "migration.discover.campaigns", icon: "📧" },
];

export function DiscoverStep({ state, updateState }: DiscoverStepProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.discoveredEntities || !state.jobId) return;

    setIsLoading(true);
    apiFetch<{ entities: Record<string, { count: number }> }>(
      `/platform/migration/${state.jobId}/discover`,
    )
      .then((result) => {
        const entities: Record<string, { count: number; selected: boolean }> = {};
        for (const [key, val] of Object.entries(result.entities)) {
          entities[key] = { count: val.count, selected: val.count > 0 };
        }
        updateState({ discoveredEntities: entities });
      })
      .catch(() => setError(t("migration.discover.errorFetch")))
      .finally(() => setIsLoading(false));
  }, [state.jobId, state.discoveredEntities, updateState, t]);

  const toggleEntity = (key: string) => {
    if (!state.discoveredEntities) return;
    const updated = { ...state.discoveredEntities };
    const current = updated[key];
    if (current) {
      updated[key] = { ...current, selected: !current.selected };
      updateState({ discoveredEntities: updated });
    }
  };

  const selectedCount = state.discoveredEntities
    ? Object.values(state.discoveredEntities).filter((e) => e.selected).length
    : 0;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{t("migration.discover.scanning")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          {t("migration.discover.title")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("migration.discover.description")}
        </p>
      </div>

      <div className="space-y-2">
        {ENTITY_TYPES.map((entity) => {
          const data = state.discoveredEntities?.[entity.key];
          if (!data) return null;

          return (
            <label
              key={entity.key}
              className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-accent/30 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={data.selected}
                onChange={() => toggleEntity(entity.key)}
                className="rounded border-border"
                disabled={data.count === 0}
              />
              <span className="text-lg">{entity.icon}</span>
              <span className="flex-1 text-sm font-medium text-foreground">
                {t(entity.labelKey)}
              </span>
              <span className="text-sm text-muted-foreground">
                {data.count.toLocaleString()}
              </span>
            </label>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        {t("migration.discover.selectedCount", { count: selectedCount })}
      </p>
    </div>
  );
}
